const DEFAULT_NAMESPACE = 'age-gate';
const DEFAULT_SELECTOR = `[data-js-${DEFAULT_NAMESPACE}]`;

const OPEN_CLASS = 'state-open';

export class AgeGate {
	constructor(node, config = {}) {
		this.elNode = node;
		this.namespace = config.namespace || DEFAULT_NAMESPACE;
		this.minAge = config.minAge || 18;

		this.pendingUrl = null;

		this.internalHosts = this._parseHosts(
			config.internalHosts || node.getAttribute(`data-js-${this.namespace}-hosts`)
		);

		this.elContent = this.elNode.querySelector(`[data-js-${this.namespace}-content]`);

		this.elForm = this.elNode.querySelector(`[data-js-${this.namespace}-form]`);

		this.elMonth = this.elNode.querySelector(`[data-js-${this.namespace}-month]`);

		this.elDay = this.elNode.querySelector(`[data-js-${this.namespace}-day]`);

		this.elYear = this.elNode.querySelector(`[data-js-${this.namespace}-year]`);

		this.elClose = this.elNode.querySelector(`[data-js-${this.namespace}-close]`);

		this.elError = this.elNode.querySelector(`[data-js-${this.namespace}-error]`);

		this.elErrorText = this.elNode.querySelector(`[data-js-${this.namespace}-error-text]`);
	}

	init() {
		if (!this.elContent || !this.elForm) return;

		this._onSubmit = this._onSubmit.bind(this);
		this._onLinkClick = this._onLinkClick.bind(this);
		this._onClose = this._onClose.bind(this);
		this._onDateChange = this._onDateChange.bind(this);

		this.elForm.addEventListener('submit', this._onSubmit);

		if (this.elClose) {
			this.elClose.addEventListener('click', this._onClose);
		}

		document.addEventListener('click', this._onLinkClick);

		this._populateYears();
		this._updateDays();

		if (this.elMonth) {
			this.elMonth.addEventListener('change', this._onDateChange);
		}

		if (this.elYear) {
			this.elYear.addEventListener('change', this._onDateChange);
		}

		this._updateDOM(false);
	}

	destroy() {
		this.elForm.removeEventListener('submit', this._onSubmit);
		document.removeEventListener('click', this._onLinkClick);

		if (this.elClose) {
			this.elClose.removeEventListener('click', this._onClose);
		}

		if (this.elMonth) {
			this.elMonth.removeEventListener('change', this._onDateChange);
		}

		if (this.elYear) {
			this.elYear.removeEventListener('change', this._onDateChange);
		}
	}

	isOpen() {
		return this.elNode.classList.contains(OPEN_CLASS);
	}

	open() {
		this._updateDOM(true);

		if (this.elMonth) {
			this.elMonth.focus();
		}
	}

	close() {
		this._reset();
		this._updateDOM(false);
	}

	_updateDOM(isOpen) {
		if (isOpen) {
			this.elNode.classList.add(OPEN_CLASS);
			this.elNode.removeAttribute('inert');
		} else {
			this.elNode.classList.remove(OPEN_CLASS);
			this.elNode.setAttribute('inert', '');
		}
	}

	_onLinkClick(event) {
		const link = event.target.closest('a');
		if (!link) return;

		const target = this._getLinkTarget(link);

		if (!target || !target.isExternal) return;

		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}

		event.preventDefault();

		this.pendingUrl = target.href;

		this.open();
	}

	_parseHosts(value) {
		if (!value) return [];

		const list = Array.isArray(value) ? value : String(value).split(',');

		return list
			.map((entry) => {
				const trimmed = String(entry).trim();

				if (!trimmed) return '';

				// Tolerate full URLs as well as bare hostnames.
				try {
					return new URL(trimmed).hostname.toLowerCase();
				} catch (error) {
					return trimmed.toLowerCase();
				}
			})
			.filter(Boolean);
	}

	// The current host always counts as internal; configured hosts cover the same
	// site under another domain, so staging can follow links that Google CSE
	// returns against the live domain.
	_isInternalHost(hostname) {
		const host = String(hostname).toLowerCase();

		if (host === window.location.hostname.toLowerCase()) return true;

		return this.internalHosts.includes(host);
	}

	_getLinkTarget(link) {
		if (!link.href) return null;

		if (link.hasAttribute('data-no-age-gate')) return null;

		const href = link.getAttribute('href');

		if (!href) return null;
		if (href.startsWith('#')) return null;
		if (href.startsWith('mailto:')) return { href: link.href, isExternal: true };
		if (href.startsWith('tel:')) return { href: link.href, isExternal: true };

		let url;

		try {
			url = new URL(link.href);
		} catch (error) {
			return null;
		}

		// Google Programmable Search rewrites result hrefs on mousedown to a
		// google.com/url?...&q=<destination> tracking redirect, so by the time the
		// click lands every result looks external. Unwrap it before comparing hosts.
		if (/(^|\.)google\.com$/.test(url.hostname) && url.pathname === '/url') {
			const destination = url.searchParams.get('q') || url.searchParams.get('url');

			if (destination) {
				try {
					url = new URL(destination, window.location.href);
				} catch (error) {
					// Unparseable destination, keep the redirect URL.
				}
			}
		}

		return { href: url.href, isExternal: !this._isInternalHost(url.hostname) };
	}

	_isExternalLink(link) {
		const target = this._getLinkTarget(link);

		return Boolean(target && target.isExternal);
	}

	_populateYears() {
		if (!this.elYear) return;

		const currentYear = new Date().getFullYear();
		const maxYear = currentYear;
		const minYear = currentYear - 100;

		for (let year = maxYear; year >= minYear; year--) {
			const option = document.createElement('option');
			option.value = year;
			option.textContent = year;
			this.elYear.appendChild(option);
		}
	}

	_onDateChange() {
		this._updateDays();
	}

	_updateDays() {
		if (!this.elDay) return;

		const month = parseInt(this.elMonth.value, 10);
		const year = parseInt(this.elYear.value, 10);

		const days = this._getDaysInMonth(month, year);

		const previous = this.elDay.value;

		this.elDay.innerHTML = '<option value="" disabled selected>Day</option>';

		for (let i = 1; i <= days; i++) {
			const option = document.createElement('option');
			option.value = i;
			option.textContent = i;

			if (previous && parseInt(previous, 10) === i) {
				option.selected = true;
			}

			this.elDay.appendChild(option);
		}
	}

	_getDaysInMonth(month, year) {
		if (!month) return 31;
		if (!year) year = new Date().getFullYear();

		return new Date(year, month, 0).getDate();
	}

	_onSubmit(event) {
		event.preventDefault();

		const month = parseInt(this.elMonth.value, 10);
		const day = parseInt(this.elDay.value, 10);
		const year = parseInt(this.elYear.value, 10);

		if (!month || !day || !year) {
			this._showError('Please enter a valid birthdate.');
			return;
		}

		const dob = new Date(year, month - 1, day);

		if (!this._isValidDate(dob, year, month, day)) {
			this._showError('Please enter a valid birthdate.');
			return;
		}

		const age = this._getAge(dob);

		if (age >= this.minAge) {
			this.close();

			if (this.pendingUrl) {
				window.location.href = this.pendingUrl;
				this.pendingUrl = null;
			}

			return;
		}

		this._showError();
	}

	_isValidDate(date, year, month, day) {
		return (
			date instanceof Date &&
			!Number.isNaN(date.getTime()) &&
			date.getFullYear() === year &&
			date.getMonth() === month - 1 &&
			date.getDate() === day
		);
	}

	_getAge(dob) {
		const today = new Date();

		let age = today.getFullYear() - dob.getFullYear();

		const birthdayPassed =
			today.getMonth() > dob.getMonth() ||
			(today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

		if (!birthdayPassed) {
			age--;
		}

		return age;
	}

	_showError() {
		if (!this.elError) return;

		this.elForm.setAttribute('hidden', '');

		this.elError.removeAttribute('hidden');
	}

	_reset() {
		if (this.elForm) {
			this.elForm.reset();
			this.elForm.removeAttribute('hidden');
		}

		if (this.elError) {
			this.elError.setAttribute('hidden', '');
			this.elErrorText.textContent = '';
		}

		this._updateDays();
	}

	_onClose() {
		this.close();
	}
}

export default function factory(selector = DEFAULT_SELECTOR) {
	const nodes = document.querySelectorAll(selector);
	if (!nodes.length) return;

	return Array.from(nodes).map((node) => {
		const module = new AgeGate(node);
		module.init();
		return module;
	});
}
